import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useEndpoints } from "../api/ApiContext";
import { ApiError, PASSWORD_POLICY_MESSAGE } from "../api/errors";
import { Icon } from "../components/Icon";
import { Banner, Button, ButtonLink, Input } from "../components/ui";
import { SUPPORT_EMAIL } from "../config";
import { checkPasswordLocally } from "../lib/passwordPolicy";
import { AuthLayout } from "./AuthLayout";
import { NewPasswordField } from "./NewPasswordField";
import { RateLimitNotice, useRateLimit } from "./rateLimit";

// Account recovery, UX-DESIGN.md §4.1 (and §4.4: security notices go to
// the recovery channel too). Routes: cmd/core-platform/recovery.go.
//
//   /recover                     hub: which problem do you have?
//   /recover/password            request a reset link  -> "check your email"
//   /recover/password#token=T    choose a new password (link from the account email)
//   /recover/mfa                 request an MFA reset  -> "check your email and recovery contact"
//   /recover/mfa#token=T         enter the recovery-contact code, then reset
//
// Token links. The backend's RecoveryNotifier must build exactly
// `<portal origin>/recover/password#token=<token>` and
// `<portal origin>/recover/mfa#token=<token>` (token URL-encoded). The
// token goes in the fragment so it never reaches a server: no access log,
// no proxy log, no Referer. `?token=` is accepted as well, and either way
// the token is moved into memory and removed from the address bar on
// arrival, so it isn't left in history or copied along with the URL.
//
// Anti-enumeration. Both request routes answer 202 for every email, and
// this screen says exactly the same thing after every 202. It never
// suggests whether the address has an account, has a recovery contact,
// or is disabled. A 429 is the same for every email too (per-account
// buckets are charged whether or not the account exists).

type Kind = "password" | "mfa";

function tokenFrom(hash: string, search: string): string | null {
  const fromHash = new URLSearchParams(hash.replace(/^#/, "")).get("token");
  const fromQuery = new URLSearchParams(search).get("token");
  return fromHash || fromQuery || null;
}

/** Reads the link token once, keeps it in memory, and strips it from the URL. */
function useLinkToken(): [string | null, () => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(() => tokenFrom(location.hash, location.search));
  const inUrl = tokenFrom(location.hash, location.search);

  useEffect(() => {
    if (inUrl === null) return;
    setToken(inUrl);
    navigate(location.pathname, { replace: true });
  }, [inUrl, location.pathname, navigate]);

  return [token, () => setToken(null)];
}

// ---------------------------------------------------------------------------
// Hub

export function RecoverPage() {
  return (
    <AuthLayout title="Can't sign in?" footer={<Link to="/login">Back to sign in</Link>}>
      <div className="stack">
        <section className="stack-sm" aria-labelledby="recover-password">
          <h2 id="recover-password" style={{ fontSize: "var(--text-h3)" }}>
            Forgot your password?
          </h2>
          <p>We'll email you a link to choose a new one.</p>
          <div>
            <ButtonLink to="/recover/password">Reset your password</ButtonLink>
          </div>
        </section>
        <section className="stack-sm" aria-labelledby="recover-mfa">
          <h2 id="recover-mfa" style={{ fontSize: "var(--text-h3)" }}>
            Lost your security key or authenticator app?
          </h2>
          <p>
            If you still have a recovery code, sign in as usual and choose <strong>Use a recovery code</strong> on the
            verification step. Each code works once.
          </p>
          <div>
            <ButtonLink to="/login">Sign in with a recovery code</ButtonLink>
          </div>
          <p>
            Out of recovery codes too? You can reset two-step verification. We'll confirm it's you through two separate
            channels: your account email and the recovery contact you gave when you signed up.
          </p>
          <div>
            <ButtonLink to="/recover/mfa">Reset two-step verification</ButtonLink>
          </div>
        </section>
        <p className="small muted">
          Can't reach your account email or your recovery contact? Contact{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </div>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Request + "check your email" (both kinds)

const REQUEST_COPY: Record<Kind, { title: string; intro: string; submit: string }> = {
  password: {
    title: "Reset your password",
    intro: "Enter the email address you sign in with. We'll send a link to choose a new password.",
    submit: "Send reset link",
  },
  mfa: {
    title: "Reset two-step verification",
    intro:
      "Use this if you've lost your security key or authenticator app and have no recovery codes left. We'll send a link to your account email and a code to your recovery contact. You'll need both.",
    submit: "Send link and code",
  },
};

function RecoveryRequest({ kind }: { kind: Kind }) {
  const ep = useEndpoints();
  const rl = useRateLimit();
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const copy = REQUEST_COPY[kind];

  async function send(address: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      if (kind === "password") await ep.auth.recovery.requestPasswordReset(address);
      else await ep.auth.recovery.requestMfaReset(address);
      return true;
    } catch (err) {
      if (!rl.capture(err)) setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(address)) {
      setFieldError("Enter the email address you sign in with.");
      return;
    }
    setFieldError(undefined);
    if (await send(address)) {
      setResent(false);
      setSentTo(address);
    }
  }

  async function resend() {
    if (sentTo && (await send(sentTo))) setResent(true);
  }

  const errorBanner = error ? (
    <Banner tone="error" live="alert">
      {error}
    </Banner>
  ) : null;

  if (sentTo) {
    return (
      <AuthLayout title="Check your email" footer={<Link to="/login">Back to sign in</Link>}>
        <div className="stack">
          {errorBanner}
          <RateLimitNotice state={rl} />
          <CheckYourEmail kind={kind} email={sentTo} />
          {resent ? (
            <Banner tone="success" live="polite">
              Sent again. Only the newest {kind === "password" ? "link works" : "link and code work"}.
            </Banner>
          ) : null}
          <div className="row">
            <Button variant="secondary" onClick={resend} loading={busy} disabled={rl.limited}>
              Send again
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setSentTo(null);
                setResent(false);
                setError(null);
              }}
            >
              Use a different email
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={copy.title} footer={<Link to="/recover">Other ways to get back in</Link>}>
      <form className="stack" onSubmit={submit} noValidate>
        <p>{copy.intro}</p>
        {errorBanner}
        <RateLimitNotice state={rl} />
        <Input
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldError}
        />
        <Button type="submit" block loading={busy} disabled={rl.limited}>
          {copy.submit}
        </Button>
      </form>
    </AuthLayout>
  );
}

/**
 * What we say after a 202. It must not depend on anything but the address
 * the user typed: the server answers identically for every email.
 */
function CheckYourEmail({ kind, email }: { kind: Kind; email: string }) {
  if (kind === "password") {
    return (
      <div className="stack-sm" data-testid="recovery-sent">
        <p>
          If <strong>{email}</strong> belongs to a VADS account, we've sent it a link to choose a new password. The link
          works once and expires in 1 hour.
        </p>
        <p className="small muted">
          Nothing arrived? Check your spam folder and that this is the address you sign in with. Sending again replaces
          the earlier link.
        </p>
      </div>
    );
  }
  return (
    <div className="stack-sm" data-testid="recovery-sent">
      <p>
        If <strong>{email}</strong> belongs to a VADS account with a separate recovery contact, we've sent two messages:
      </p>
      <ol className="stack-sm">
        <li>
          A <strong>link</strong> to {email}. Open it to continue.
        </li>
        <li>
          A <strong>code</strong> to your recovery contact, the other email address or phone number you gave when you
          signed up. You'll enter it after opening the link.
        </li>
      </ol>
      <p className="small muted">
        Both expire in 1 hour, and sending again replaces them. If the account has no separate recovery contact, the email
        explains how to reach support instead.
      </p>
    </div>
  );
}

function InvalidLink({ kind, onRestart }: { kind: Kind; onRestart: () => void }) {
  return (
    <AuthLayout title="This link doesn't work any more" footer={<Link to="/login">Back to sign in</Link>}>
      <div className="stack">
        <Banner tone="error" live="alert">
          Reset links work once and expire after an hour. Asking for a new one also cancels the old one.
        </Banner>
        <div>
          <Button onClick={onRestart}>{kind === "password" ? "Request a new link" : "Start the reset again"}</Button>
        </div>
      </div>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Password reset

export function PasswordRecoveryPage() {
  const [token, clearToken] = useLinkToken();
  return token ? <SetNewPassword token={token} onRestart={clearToken} /> : <RecoveryRequest kind="password" />;
}

function SetNewPassword({ token, onRestart }: { token: string; onRestart: () => void }) {
  const ep = useEndpoints();
  const rl = useRateLimit();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<"form" | "invalid" | "done">("form");

  async function submit(e: FormEvent) {
    e.preventDefault();
    const fe: typeof errors = {};
    const local = checkPasswordLocally(password);
    if (local) fe.password = PASSWORD_POLICY_MESSAGE[local];
    if (!fe.password && confirm !== password) fe.confirm = "The passwords don't match.";
    setErrors(fe);
    if (fe.password || fe.confirm) return;
    setBusy(true);
    setError(null);
    try {
      await ep.auth.recovery.completePasswordReset(token, password);
      setStatus("done");
    } catch (err) {
      if (rl.capture(err)) return;
      if (err instanceof ApiError && err.code === "recovery_token_invalid") setStatus("invalid");
      // A refused password leaves the token unused (recovery.go), so the user just picks another.
      else if (err instanceof ApiError && err.code === "password_policy") setErrors({ password: err.message });
      else setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (status === "invalid") return <InvalidLink kind="password" onRestart={onRestart} />;

  if (status === "done") {
    return (
      <AuthLayout title="Password changed">
        <div className="stack">
          <Banner tone="success" title="Your password has been changed">
            We've signed you out on every device and sent a security notice to your account email and your recovery
            contact. If two-step
            verification is on, you'll still need it to sign in.
          </Banner>
          <Link className="btn btn-primary btn-block" to="/login">
            Sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password" footer={<Link to="/login">Back to sign in</Link>}>
      <form className="stack" onSubmit={submit} noValidate>
        {error ? (
          <Banner tone="error" live="alert">
            {error}
          </Banner>
        ) : null}
        <RateLimitNotice state={rl} />
        <NewPasswordField label="New password" value={password} onChange={setPassword} error={errors.password} />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={errors.confirm}
        />
        <p className="small muted">Changing your password signs you out everywhere.</p>
        <Button type="submit" block loading={busy} disabled={rl.limited}>
          Change password
        </Button>
      </form>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// MFA reset: both channels must confirm (internal/auth/recovery.go).
// The account-email link carries the token (opening it is channel 1); the
// code sent to the recovery contact is channel 2 and must be confirmed
// (POST .../confirm-contact) before the token will complete the reset
// (POST .../complete answers 409 recovery_contact_unconfirmed otherwise).

export function MfaRecoveryPage() {
  const [token, clearToken] = useLinkToken();
  return token ? <MfaResetConfirm token={token} onRestart={clearToken} /> : <RecoveryRequest kind="mfa" />;
}

function MfaResetConfirm({ token, onRestart }: { token: string; onRestart: () => void }) {
  const ep = useEndpoints();
  const rl = useRateLimit();
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set once confirm-contact succeeds, so a retry after a failed
  // completion doesn't re-send a code that has already been used.
  const [contactConfirmed, setContactConfirmed] = useState(false);
  const [status, setStatus] = useState<"form" | "invalid" | "done">("form");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!contactConfirmed) {
      const value = code.trim();
      if (!value) {
        setCodeError("Enter the code sent to your recovery contact.");
        return;
      }
      setCodeError(undefined);
      setBusy(true);
      try {
        await ep.auth.recovery.confirmContact(value);
        setContactConfirmed(true);
      } catch (err) {
        setBusy(false);
        if (rl.capture(err)) return;
        if (err instanceof ApiError && err.code === "recovery_token_invalid")
          setCodeError(
            "That code isn't valid. Check you've typed the newest one; codes work once, expire after an hour, and are replaced when you ask again.",
          );
        else setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
        return;
      }
    }
    setBusy(true);
    try {
      await ep.auth.recovery.completeMfaReset(token);
      setStatus("done");
    } catch (err) {
      if (rl.capture(err)) return;
      if (err instanceof ApiError && err.code === "recovery_token_invalid") setStatus("invalid");
      else if (err instanceof ApiError && err.code === "recovery_contact_unconfirmed") {
        setContactConfirmed(false);
        setCode("");
        setCodeError(err.message);
      } else setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (status === "invalid") return <InvalidLink kind="mfa" onRestart={onRestart} />;

  if (status === "done") {
    return (
      <AuthLayout title="Two-step verification reset">
        <div className="stack">
          <Banner tone="success" title="Your verification methods have been removed">
            We removed your authenticator app, security keys and unused recovery codes, signed you out on every device, and
            sent a notice to your account email and recovery contact.
          </Banner>
          <p>
            Sign in with your email and password. You'll be asked to set up a new security key or authenticator app first:
            wallet and payout screens stay locked until you do.
          </p>
          <Link className="btn btn-primary btn-block" to="/login">
            Sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset two-step verification" footer={<Link to="/login">Back to sign in</Link>}>
      <form className="stack" onSubmit={submit} noValidate>
        <ul className="progress-list" aria-label="Identity checks">
          <li className="progress-item">
            <Icon name="checkCircle" />
            <span>
              Link from your account email — <strong>opened</strong>
            </span>
          </li>
          <li className="progress-item">
            <Icon name={contactConfirmed ? "checkCircle" : "circle"} />
            <span>
              Code from your recovery contact — <strong>{contactConfirmed ? "confirmed" : "not entered yet"}</strong>
            </span>
          </li>
        </ul>
        {error ? (
          <Banner tone="error" live="alert">
            {error}
          </Banner>
        ) : null}
        <RateLimitNotice state={rl} />
        <Banner tone="warning" title="This removes every verification method on your account">
          Your authenticator app, security keys and recovery codes stop working, and you're signed out everywhere. You'll set
          up a new method when you next sign in.
        </Banner>
        {contactConfirmed ? null : (
          <Input
            label="Code from your recovery contact"
            hint="We sent it to the other email address or phone number you gave when you signed up."
            autoComplete="one-time-code"
            mono
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={codeError}
          />
        )}
        <Button type="submit" variant="danger" block loading={busy} disabled={rl.limited}>
          {contactConfirmed ? "Finish reset" : "Confirm code and reset"}
        </Button>
      </form>
    </AuthLayout>
  );
}
